<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  stats: Record<string, number>;
  choices: Array<{ id: number, text: string, translations?: Record<string, { text: string }> }>;
}

const props = defineProps<Props>();
const { t, locale } = useI18n();

const getChoiceText = (choice: { id: number, text: string, translations?: Record<string, { text: string }> }) => {
  if (choice.translations && choice.translations[locale.value]) {
    return choice.translations[locale.value].text;
  }
  return choice.text;
};

const totalVotes = computed(() => {
  return Object.values(props.stats).reduce((acc, count) => acc + count, 0);
});

const statsData = computed(() => {
  return props.choices.map(choice => {
    const count = props.stats[String(choice.id)] || 0;
    const percentage = totalVotes.value > 0 ? Math.round((count / totalVotes.value) * 100) : 0;
    return {
      ...choice,
      displayText: getChoiceText(choice), // Use localized text
      count,
      percentage
    };
  });
});
</script>

<template>
  <div class="answer-stats-overlay">
    <div class="answer-stats">
      <h3>{{ t('stats.distribution_title') }}</h3>
      <div class="stats-grid">
        <div v-for="item in statsData" :key="item.id" class="stat-item">
          <div class="stat-label">{{ item.displayText }}</div>
          <div class="stat-bar-container">
            <div class="stat-bar" :style="{ width: item.percentage + '%' }"></div>
            <span class="stat-value">{{ item.percentage }}% ({{ item.count }}{{ t('stats.people_suffix') }})</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.answer-stats-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.answer-stats {
  width: 90%;
  max-width: 600px;
  background-color: #fff;
  /* Opaque white */
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #333;
  text-align: center;
}

.stats-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stat-label {
  font-weight: bold;
  margin-bottom: 0.2rem;
  color: #555;
}

.stat-bar-container {
  width: 100%;
  height: 24px;
  background-color: #e0e0e0;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.stat-bar {
  height: 100%;
  background-color: #007bff;
  border-radius: 12px;
  transition: width 0.5s ease-out;
}

.stat-value {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  font-size: 0.85rem;
  color: #333;
  font-weight: bold;
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.8);
}
</style>
